import React, { useEffect, useState } from 'react';
import { Avatar, Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DisplayValue } from '../../devices/shared-ui';
import DeviceLoadingSkeleton from '../../devices/shared-ui/deviceLoadingSkeleton';
import {
  DeviceInfo,
  DeviceInformation,
} from '../../devices/shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../../devices/shared-ui/alerts-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useDeviceHasAlerts } from '../../../system/AlertsManager';
import moment from 'moment';
import {
  EnergyStorageBatterySystemDetailQuery,
  useEnergyStorageBatterySystemDetailQuery,
  useEnergyStorageBatterySystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { formatDeviceMetric } from '../../../utils/formatters';
import { SolarManagementSystemComponent } from '../../devices/virtual-power-plant/solar-management-system';
import { EnergyControllerComponent } from '../../devices/virtual-power-plant/energy-controller';
import { EnergyInverterComponent } from '../../devices/virtual-power-plant/energy-inverter';
import { EnergyStorageCoolingSystemComponent } from '../../devices/virtual-power-plant/energy-storage-cooling-system';
import { FireSuppressionSystemComponent } from '../../devices/virtual-power-plant/fire-suppression-system';
import { EnergyConverterComponent } from '../../devices/virtual-power-plant/energy-converter';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const BatterySystemIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"
      fill="currentColor"
    />
  </svg>
);

export function EnergyStorageBatterySystemComponent({
  participantId,
}: {
  participantId: string;
}) {
  const { data, loading, error } = useEnergyStorageBatterySystemDetailQuery({
    variables: { id: participantId },
  });

  const [participant, setParticipant] =
    useState<
      Partial<
        EnergyStorageBatterySystemDetailQuery['energyStorageBatterySystemById']
      >
    >();
  const [expanded, setExpanded] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [participantInfo, setParticipantInfo] = useState<DeviceInformation[]>(
    [],
  );

  const hasAlerts = useDeviceHasAlerts({ deviceId: participant?._id });
  const isConnected = participant?.connected?.value ?? false;

  useEnergyStorageBatterySystemDetailUpdatesSubscription({
    variables: { id: participantId },
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyStorageBatterySystemById) {
      setParticipant(data.energyStorageBatterySystemById);
    }
  }, [data]);

  useEffect(() => {
    if (participant) {
      const panelInfo: DeviceInformation = {
        'Power State': (participant.powerState?.value ?? 'Unknown').toString(),
        'Connection Status': (
          participant.connected?.value ?? 'Unknown'
        ).toString(),
        'Last Update': participant.timestamp
          ? moment(participant.timestamp).fromNow()
          : 'Unknown',
      };

      const systemInfo: DeviceInformation = {
        'Maximum Energy Capacity': formatDeviceMetric({
          value: participant.maximumEnergyCapacity?.value,
          units: participant.maximumEnergyCapacity?.units ?? 'kWh',
        }),
        'Inverter ID': participant.inverterId ?? 'Not assigned',
        'Controller ID': participant.controllerId ?? 'Not assigned',
        'Cooling System ID': participant.coolingSystemId ?? 'Not assigned',
        'Fire Suppression System ID':
          participant.fireSuppressionSystemId ?? 'Not assigned',
        'Converter ID': participant.converterId ?? 'Not assigned',
      };

      const participantsInfo: DeviceInformation = {
        'Participant ID': participant._id ?? 'Unidentified',
        Name: participant.name ?? 'Unnamed',
        Location: participant.location ?? 'Unknown',
        Status: (participant.updateStatus ?? 'Unknown').toString(),
        'Last Updated': participant.timestamp
          ? moment(participant.timestamp).fromNow()
          : 'Unknown',
      };

      setParticipantInfo([panelInfo, systemInfo, participantsInfo]);
    }
  }, [participant]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return error ? (
    <div>Error</div>
  ) : loading ? (
    <DeviceLoadingSkeleton />
  ) : participant ? (
    <Card
      sx={{
        backgroundColor: hasAlerts
          ? '#ffd5d5'
          : isConnected
            ? 'rgba(0, 255, 0, 0.05)'
            : '#ebf7f6',
        boxShadow: 1,
        p: 0,
        width: '100%',
        border: hasAlerts ? '1px solid red' : '',
        borderRadius: '4px',
        marginTop: '4px',
      }}
    >
      <CardHeader
        sx={{
          paddingTop: '0px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingBottom: '0px',
          border: '1px solid rgba(0,0,0, 0.1)',
        }}
        title={
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ padding: '0px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingTop: '6px',
                  }}
                >
                  <Typography
                    variant="h5"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      paddingTop: '3px',
                    }}
                  >
                    {participant?.name}
                  </Typography>
                </div>
              </Box>
            </div>
            <div style={{ flex: '1 1 auto', flexDirection: 'column' }} />
            <div style={{ flex: '0 0 auto', flexDirection: 'column' }}>
              <Box sx={{ p: 0 }}>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <div style={{ marginLeft: '6px' }}>&nbsp;</div>
                  <DisplayValue
                    value={participant.maximumEnergyCapacity?.value}
                    units={participant.maximumEnergyCapacity?.units ?? 'kWh'}
                    size={'h3'}
                    unitsSize={'14px'}
                    style={{
                      paddingTop: '8px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    }}
                  />
                  {participant.batteryManagementSystemId ? (
                    <SolarManagementSystemComponent
                      id={participant.batteryManagementSystemId}
                    />
                  ) : null}
                  {participant.controllerId ? (
                    <EnergyControllerComponent id={participant.controllerId} />
                  ) : null}
                  {participant.inverterId ? (
                    <EnergyInverterComponent id={participant.inverterId} />
                  ) : null}
                  {participant.coolingSystemId ? (
                    <EnergyStorageCoolingSystemComponent
                      id={participant.coolingSystemId}
                    />
                  ) : null}
                  {participant.fireSuppressionSystemId ? (
                    <FireSuppressionSystemComponent
                      id={participant.fireSuppressionSystemId}
                    />
                  ) : null}
                  {participant.converterId ? (
                    <EnergyConverterComponent id={participant.converterId} />
                  ) : null}
                  <div style={{ marginRight: '6px' }}>&nbsp;</div>
                </Stack>
              </Box>
            </div>
          </div>
        }
        avatar={
          <Avatar
            sx={{
              backgroundColor: 'transparent',
              padding: '0px',
              margin: '0px',
            }}
          >
            <BatterySystemIcon />
          </Avatar>
        }
        action={
          <Stack direction="row" spacing={1}>
            <ToggleAlertDetailsButton
              expand={alertsExpanded}
              onClick={() => setAlertsExpanded(!alertsExpanded)}
              device={participant}
              buttonLabel="Alerts"
            />
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </Stack>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceInfo infoEntries={participantInfo} />
        </Box>
      </Collapse>
      <Collapse in={alertsExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <DeviceAlerts device={participant} alertsExpanded={alertsExpanded} />
        </Box>
      </Collapse>
    </Card>
  ) : null;
}
